/**
 * Iter 71 — Reusable Drive attachments widget.
 *
 * Drop into any page that needs to attach documents to an entity:
 *   <DriveAttachments contextType="project" contextId={p.id} category="Drawings" />
 *
 * Behaviour:
 *  - Shows a "Connect Google Drive" CTA if no Drive is connected anywhere.
 *  - Otherwise renders an "Upload to Drive" button + a list of files
 *    already linked to (contextType, contextId).
 *  - Files are stored in the connected admin's Drive — multi-GB capable.
 *  - Multi-file upload supported. Live progress per file.
 */
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import { Cloud, Upload, ExternalLink, Trash2, Loader2, HardDrive, FileText, Link2 } from 'lucide-react';
import { apiGet, apiDel, API } from '../lib/api';
import { useConfirm } from './ConfirmDialog';
import { formatDateTime as _fmtDT } from '../lib/format';

function fmtSize(bytes) {
  const b = parseInt(bytes || 0, 10);
  if (!b) return '—';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  if (b < 1024 * 1024 * 1024) return `${(b / 1024 / 1024).toFixed(1)} MB`;
  return `${(b / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

const fmtDate = _fmtDT;

export default function DriveAttachments({
  contextType,
  contextId,
  category,
  folderName = 'Ellora EPC',
  accept,
  multiple = true,
  title = 'Google Drive Attachments',
  compact = false,
  onChanged,
}) {
  const [status, setStatus] = useState(null);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploads, setUploads] = useState([]); // [{name, progress, error?}]
  const inputRef = useRef(null);
  const [confirm, ConfirmHost] = useConfirm();

  const reload = useCallback(async () => {
    if (!contextType || !contextId) return;
    try {
      const params = new URLSearchParams({ context_type: contextType, context_id: contextId });
      if (category) params.set('category', category);
      const r = await apiGet(`/drive/attachments?${params}`);
      setFiles(r.attachments || []);
    } catch (e) {
      console.error('Drive attachments fetch failed', e);
    }
  }, [contextType, contextId, category]);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const s = await apiGet('/drive/status');
        if (!alive) return;
        setStatus(s);
        if (s?.available) await reload();
      } catch (e) {
        console.error(e);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [reload]);

  const uploadOne = (file) => new Promise((resolve, reject) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('folder_name', folderName);
    if (contextType) fd.append('context_type', contextType);
    if (contextId) fd.append('context_id', contextId);
    if (category) fd.append('category', category);

    const token = localStorage.getItem('access_token');
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${API}/drive/upload`);
    xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.upload.onprogress = (ev) => {
      if (ev.lengthComputable) {
        const pct = Math.round((ev.loaded / ev.total) * 100);
        setUploads((u) => u.map((row) => row.name === file.name ? { ...row, progress: pct } : row));
      }
    };
    xhr.onload = () => xhr.status < 300 ? resolve(JSON.parse(xhr.responseText)) : reject(xhr.responseText);
    xhr.onerror = () => reject('Network error');
    xhr.send(fd);
  });

  const onPick = async (e) => {
    const picked = Array.from(e.target.files || []);
    if (!picked.length) return;
    setUploads(picked.map((f) => ({ name: f.name, progress: 0 })));
    for (const f of picked) {
      try {
        await uploadOne(f);
        setUploads((u) => u.map((row) => row.name === f.name ? { ...row, progress: 100 } : row));
      } catch (err) {
        setUploads((u) => u.map((row) => row.name === f.name ? { ...row, error: String(err).slice(0, 80) } : row));
        toast.error(`"${f.name}" failed to upload`);
      }
    }
    toast.success(`Uploaded ${picked.length} file${picked.length > 1 ? 's' : ''} to Drive`);
    if (inputRef.current) inputRef.current.value = '';
    await reload();
    onChanged?.();
    setTimeout(() => setUploads([]), 2000);
  };

  const onDelete = async (att) => {
    const ok = await confirm({
      title: 'Remove file from Drive?',
      body: `"${att.name}" will be moved to the Drive trash and unlinked from this record.\n\nThis cannot be undone via this app (you'd need to restore from Google Drive directly).`,
      confirmLabel: 'Remove',
      danger: true,
    });
    if (!ok) return;
    try {
      await apiDel(`/drive/attachments/${att.id}`);
      toast.success('Removed');
      await reload();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to remove');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-xs text-slate-500 py-3">
        <Loader2 size={12} className="animate-spin" /> Loading Drive…
      </div>
    );
  }

  if (!status?.configured) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-sm p-3 text-xs text-amber-900" data-testid="drive-not-configured">
        <div className="flex items-center gap-2 font-display font-bold uppercase tracking-wider text-[10px] mb-1">
          <Cloud size={12} /> Drive not configured
        </div>
        <p>Google Drive integration is not set up on the server. Ask the super admin to configure it in Settings → Cloud Storage.</p>
      </div>
    );
  }

  if (!status?.available) {
    return (
      <div className="bg-slate-50 border border-slate-200 rounded-sm p-3 text-xs text-slate-700" data-testid="drive-not-connected">
        <div className="flex items-center gap-2 font-display font-bold uppercase tracking-wider text-[10px] mb-1">
          <Cloud size={12} /> Drive not connected
        </div>
        <p>
          Cloud storage isn't connected yet. Ask the super admin to{' '}
          <a href="/app/settings/drive" className="underline text-[#3b4872] font-bold">connect Google Drive</a> to start
          attaching big files (PDFs, DWGs, photos) to this record.
        </p>
      </div>
    );
  }

  return (
    <div className={compact ? 'space-y-2' : 'space-y-3'} data-testid="drive-attachments">
      {ConfirmHost}
      {!compact && (
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <HardDrive size={14} className="text-[#3b4872]" />
            <h3 className="font-display font-bold text-sm">{title}</h3>
            {category && <span className="tag bg-indigo-100 text-indigo-700 text-[10px]">{category}</span>}
            <span className="text-[10px] text-slate-500">· {files.length} file{files.length === 1 ? '' : 's'}</span>
          </div>
          <label className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-display font-bold uppercase tracking-wider bg-[#3b4872] text-white rounded-sm cursor-pointer hover:bg-[#2d3856]" data-testid="drive-upload-btn">
            <Upload size={11} /> Upload to Drive
            <input
              ref={inputRef}
              type="file"
              hidden
              multiple={multiple}
              accept={accept}
              onChange={onPick}
              data-testid="drive-upload-input"
            />
          </label>
        </div>
      )}

      {compact && (
        <label className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[10px] font-display font-bold uppercase tracking-wider bg-[#3b4872] text-white rounded-sm cursor-pointer hover:bg-[#2d3856]" data-testid="drive-upload-btn-compact">
          <Upload size={10} /> Drive
          <input
            ref={inputRef}
            type="file"
            hidden
            multiple={multiple}
            accept={accept}
            onChange={onPick}
            data-testid="drive-upload-input"
          />
        </label>
      )}

      {/* Active uploads */}
      {uploads.length > 0 && (
        <div className="space-y-1.5">
          {uploads.map((u) => (
            <div key={u.name} className="border border-slate-200 rounded-sm p-2 bg-slate-50">
              <div className="flex items-center justify-between text-[11px] mb-1">
                <span className="font-mono truncate">{u.name}</span>
                <span className={u.error ? 'text-rose-600' : 'text-slate-600'}>
                  {u.error ? `✗ ${u.error}` : `${u.progress}%`}
                </span>
              </div>
              <div className="h-1 bg-slate-200 rounded-full overflow-hidden">
                <div
                  className={`h-full transition-all ${u.error ? 'bg-rose-500' : 'bg-[#3b4872]'}`}
                  style={{ width: `${u.progress}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* File list */}
      {files.length === 0 ? (
        <div className="text-[11px] text-slate-500 italic py-2">No Drive files attached yet.</div>
      ) : (
        <ul className="border border-slate-200 rounded-sm divide-y divide-slate-100 bg-white">
          {files.map((f) => (
            <li key={f.id} className="flex items-center gap-2 px-2.5 py-1.5 hover:bg-slate-50" data-testid={`drive-att-${f.id}`}>
              <FileText size={13} className="text-[#3b4872] shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono text-xs truncate" title={f.name}>{f.name}</span>
                  {f.category && <span className="tag bg-indigo-50 text-indigo-700 text-[9px]">{f.category}</span>}
                </div>
                <div className="text-[10px] text-slate-500 flex items-center gap-2 flex-wrap">
                  <span>{fmtSize(f.size)}</span>
                  <span>·</span>
                  <span>{f.uploaded_by_name}</span>
                  <span>·</span>
                  <span>{fmtDate(f.uploaded_at)}</span>
                </div>
                {f.folder_path && (
                  <div className="text-[10px] text-slate-400 mt-0.5 font-mono truncate" title={f.folder_path}>
                    📂 {f.folder_path}
                  </div>
                )}
              </div>
              <a
                href={f.view_link}
                target="_blank"
                rel="noreferrer"
                className="text-[#3b4872] hover:text-[#0F172A]"
                title="Open in Google Drive"
                data-testid={`drive-att-open-${f.id}`}
              >
                <ExternalLink size={13} />
              </a>
              <button
                onClick={() => onDelete(f)}
                className="text-slate-400 hover:text-rose-600"
                title="Remove"
                data-testid={`drive-att-del-${f.id}`}
              >
                <Trash2 size={13} />
              </button>
            </li>
          ))}
        </ul>
      )}

      {!compact && status?.shared_drive_email && (
        <div className="text-[10px] text-slate-400 flex items-center gap-1">
          <Link2 size={9} /> Files saved to shared Drive: <span className="font-mono">{status.shared_drive_email}</span>
        </div>
      )}
    </div>
  );
}
